/**
 * Dispute Flow page (`/deals/:shortId/dispute`).
 *
 * Wave C page 2 of 3 — handles two distinct phases of a dispute behind a
 * single route. Which phase renders is derived from `deal.status`:
 *
 *   Phase A — open  : status is `active` or `delivered`
 *                     -> 3-step wizard (reason -> details -> confirm)
 *                     -> POST /deals/:shortId/dispute
 *
 *   Phase B — view  : status is `disputed` or `resolved`
 *                     -> dispute status banner, original reason
 *                     -> evidence submission form (when disputeId is known)
 *                     -> evidence list placeholder (backend gap, see below)
 *                     -> outcome card (split / win / cancel) when resolved
 *
 * Phase A vs Phase B detection
 * ────────────────────────────
 * The deal row is the single source of truth. We do NOT trust the URL: a
 * stale browser tab might land here on a deal that has since been
 * cancelled, completed, or never been disputable. We refetch the deal,
 * then map status -> phase. Any status not in the disputable set falls
 * through to a hard "not disputable" error state.
 *
 * Backend gaps (Phase 3 Wave C)
 * ─────────────────────────────
 *  1. `POST /deals/:shortId/dispute` returns `{ dealId, reason }` with
 *     NO `disputeId`. The current `dealService.openDispute()` only
 *     freezes the contract and transitions the deal to `disputed`; the
 *     `disputes` row is created lazily by a separate service flow.
 *     -> Until the API surfaces the disputeId we cannot reliably load
 *     the dispute object after opening. We surface a warning card and
 *     ask the user to refresh in a moment.
 *
 *  2. There is no `disputes.listEvidence(disputeId)` wrapper in
 *     `web/src/lib/api/disputes.ts` and no backend `GET
 *     /disputes/:id/evidence` route either. Evidence list shows a
 *     "coming soon" placeholder.
 *
 * BigInt safety
 * ─────────────
 * Outcome card amounts are wire-format strings -> `BigInt(...)` ->
 * `nanoToTon(..., 4)`. Never `Number()`/`parseFloat()` on money fields.
 *
 * Conventions inherited from peer pages (DealDetailPage, NewDealWizard):
 *  - AppShell wrapper, Telegram MainButton for primary CTA
 *  - haptics.* on every user action
 *  - useT() for every user-facing string (no hardcoded copy)
 *  - Glassmorphism cards, danger tint on the warning/disclaimer
 *  - Slide-track wizard pattern (translate inner track horizontally)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Paperclip,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

import {
  deals as dealsApi,
  disputes as disputesApi,
  isApiError,
  users,
} from '@/lib/api';
import type {
  Deal,
  DealStatus,
  Dispute,
  DisputeStatus,
  Evidence,
  EvidenceType,
  OpenDisputeBody,
  OpenDisputeResponse,
  SubmitEvidenceBody,
  UserProfile,
} from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { nanoToTon } from '@/lib/ton/format';
import { formatDateTime } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { useMainButton } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/routes/router';
import { DisputeChat } from '@/components/common/DisputeChat';

// ─── Types & constants ──────────────────────────────────────

type Phase = 'open' | 'view';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; deal: Deal; phase: Phase }
  | { kind: 'not-found' }
  | { kind: 'not-disputable' }
  | { kind: 'error'; message: string };

type StepIndex = 0 | 1 | 2;
const STEP_COUNT = 3;
const TOTAL_STEPS_LITERAL = '3';

const DISPUTABLE_STATUSES: ReadonlySet<DealStatus> = new Set<DealStatus>([
  'active',
  'delivered',
]);

const EXISTING_DISPUTE_STATUSES: ReadonlySet<DealStatus> = new Set<DealStatus>([
  'disputed',
  'resolved',
]);

const REASON_MIN_LENGTH = 10;
/**
 * Backend cap is 5000 (see src/api/validators/dispute.schema.ts), but we
 * keep the UI cap at 2000 to encourage focused, arbiter-friendly reasons
 * and to keep textareas usable on mobile.
 */
const REASON_MAX_LENGTH = 2000;
const EVIDENCE_MAX_LENGTH = 50_000;

type ReasonPreset =
  | 'notDelivered'
  | 'qualityMismatch'
  | 'unreachable'
  | 'scopeDisagreement'
  | 'other';

const REASON_PRESETS: readonly ReasonPreset[] = [
  'notDelivered',
  'qualityMismatch',
  'unreachable',
  'scopeDisagreement',
  'other',
] as const;

const EVIDENCE_TYPES: readonly EvidenceType[] = [
  'text',
  'link',
  'image',
  'file',
] as const;

type DisputeStatusBadgeVariant = 'default' | 'gold' | 'success' | 'danger';

const DISPUTE_STATUS_VARIANT: Record<DisputeStatus, DisputeStatusBadgeVariant> =
  {
    open: 'gold',
    evidence: 'default',
    review: 'default',
    resolved: 'success',
    appealed: 'gold',
    closed: 'danger',
  };

// ─── Step indicator (mirrors NewDealWizardPage) ─────────────

interface StepIndicatorProps {
  current: StepIndex;
}

function StepIndicator({ current }: StepIndicatorProps): JSX.Element {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={STEP_COUNT}
      aria-valuenow={current + 1}
      className="flex items-center justify-center gap-2"
    >
      {Array.from({ length: STEP_COUNT }).map((_, idx) => (
        <span
          key={idx}
          aria-hidden
          className={cn(
            'h-2 rounded-full transition-all duration-300',
            idx === current
              ? 'w-6 bg-accent-gold shadow-glow-gold'
              : idx < current
                ? 'w-2 bg-accent-purple/70'
                : 'w-2 bg-border',
          )}
        />
      ))}
    </div>
  );
}

// ─── Reason preset card ─────────────────────────────────────

interface ReasonCardProps {
  preset: ReasonPreset;
  active: boolean;
  label: string;
  onSelect: (preset: ReasonPreset) => void;
}

function ReasonCard({
  preset,
  active,
  label,
  onSelect,
}: ReasonCardProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        haptics.selectionChanged();
        onSelect(preset);
      }}
      className={cn(
        'w-full rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
        'active:scale-[0.98]',
        active
          ? 'border-accent-gold/80 bg-accent-gold/10 text-text-primary'
          : 'border-border bg-bg-secondary/40 text-text-secondary hover:text-text-primary',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span>{label}</span>
        {active && (
          <CheckCircle2
            className="h-4 w-4 shrink-0 text-accent-gold"
            aria-hidden
          />
        )}
      </div>
    </button>
  );
}

// ─── Evidence type pill ─────────────────────────────────────

interface EvidenceTypePillProps {
  type: EvidenceType;
  active: boolean;
  label: string;
  onSelect: (type: EvidenceType) => void;
}

function EvidenceTypePill({
  type,
  active,
  label,
  onSelect,
}: EvidenceTypePillProps): JSX.Element {
  const Icon =
    type === 'text'
      ? FileText
      : type === 'link'
        ? LinkIcon
        : type === 'image'
          ? ImageIcon
          : Paperclip;

  return (
    <button
      type="button"
      onClick={() => {
        haptics.selectionChanged();
        onSelect(type);
      }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
        active
          ? 'border-accent-purple/60 bg-accent-purple/15 text-accent-purple'
          : 'border-border bg-bg-secondary/50 text-text-secondary hover:text-text-primary',
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </button>
  );
}

// ─── Skeleton ───────────────────────────────────────────────

function DisputeSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </Card>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────

/** Format a wire-format nanoton string into "X.XXXX TON". */
function formatNanoString(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return `${nanoToTon(BigInt(raw), 4)} TON`;
  } catch {
    return null;
  }
}

function derivePhase(status: DealStatus): Phase | null {
  if (DISPUTABLE_STATUSES.has(status)) return 'open';
  if (EXISTING_DISPUTE_STATUSES.has(status)) return 'view';
  return null;
}

// ─── Page ───────────────────────────────────────────────────

export function DisputeFlowPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ shortId: string }>();
  const shortId = params.shortId ?? '';

  // ── Mount tracking ──────────────────────────────────────
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Current user (for "You" indicator) ──────────────────
  const userSnapshot = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    userSnapshot?.id ?? null,
  );

  useEffect(() => {
    if (currentUserId) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const { user } = await users.getMe();
        if (cancelled) return;
        setCurrentUserId(user.id);
        const snapshot: UserProfile = user;
        setUser({
          id: snapshot.id,
          telegramId: snapshot.telegramId,
          username: snapshot.username,
          tonAddress: snapshot.tonAddress,
          reputationScore: snapshot.reputationScore,
          role: snapshot.role,
          isBanned: snapshot.isBanned,
        });
      } catch {
        // Falls back to anonymous "You"-less rendering.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, setUser]);

  // ── Deal load state ─────────────────────────────────────
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const loadDeal = useCallback(
    async (signal?: AbortSignal): Promise<Deal | null> => {
      try {
        const { deal } = await dealsApi.getDeal(shortId);
        if (signal?.aborted || !mountedRef.current) return null;
        const phase = derivePhase(deal.status);
        if (!phase) {
          setState({ kind: 'not-disputable' });
          return null;
        }
        setState({ kind: 'ready', deal, phase });
        return deal;
      } catch (err) {
        if (signal?.aborted || !mountedRef.current) return null;
        if (
          isApiError(err) &&
          (err.code === 'deal_not_found' || err.status === 404)
        ) {
          setState({ kind: 'not-found' });
          return null;
        }
        const message =
          err instanceof Error ? err.message : t('dispute.error.loading');
        setState({ kind: 'error', message });
        return null;
      }
    },
    [shortId, t],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    void loadDeal(ctrl.signal);
    return () => ctrl.abort();
  }, [loadDeal]);

  const refresh = useCallback(async (): Promise<void> => {
    await loadDeal();
  }, [loadDeal]);

  // ── Phase A (open) state machine ────────────────────────
  const [step, setStep] = useState<StepIndex>(0);
  const [reasonPreset, setReasonPreset] = useState<ReasonPreset | null>(null);
  const [reasonText, setReasonText] = useState<string>('');
  const [openSubmitting, setOpenSubmitting] = useState<boolean>(false);
  const [openError, setOpenError] = useState<string | null>(null);
  /**
   * After a successful POST /deals/:shortId/dispute, the deal status flips
   * to `disputed`. The backend currently does NOT return the disputeId, so
   * we surface a `lastSubmittedDisputeId` (best-effort) for Phase B's
   * evidence form. If the response ever starts including a disputeId we
   * can plug it straight in.
   */
  const [lastSubmittedDisputeId, setLastSubmittedDisputeId] = useState<
    string | null
  >(null);

  // Reset wizard state when navigating to a new deal id.
  useEffect(() => {
    setStep(0);
    setReasonPreset(null);
    setReasonText('');
    setOpenError(null);
    setLastSubmittedDisputeId(null);
  }, [shortId]);

  const isStepValid = useCallback(
    (currentStep: StepIndex): boolean => {
      switch (currentStep) {
        case 0:
          return reasonPreset !== null;
        case 1: {
          const len = reasonText.trim().length;
          return len >= REASON_MIN_LENGTH && len <= REASON_MAX_LENGTH;
        }
        case 2:
          return reasonPreset !== null && isStepValidDetails(reasonText);
        default:
          return false;
      }
    },
    [reasonPreset, reasonText],
  );

  const handleReasonPreset = useCallback(
    (preset: ReasonPreset): void => {
      setReasonPreset(preset);
      // Pre-fill the textarea with the localised label so the user has a
      // sensible starting point for the details step. "Other" stays blank.
      if (preset !== 'other' && reasonText.trim().length === 0) {
        setReasonText(t(`dispute.phase.open.reason.${preset}`));
      }
    },
    [reasonText, t],
  );

  const goNextStep = useCallback((): void => {
    if (!isStepValid(step)) return;
    haptics.light();
    if (step < STEP_COUNT - 1) {
      setStep((prev) => (prev + 1) as StepIndex);
    }
  }, [isStepValid, step]);

  const submitDispute = useCallback(async (): Promise<void> => {
    if (state.kind !== 'ready' || state.phase !== 'open') return;
    if (!isStepValid(2)) return;
    if (openSubmitting) return;

    haptics.medium();
    setOpenSubmitting(true);
    setOpenError(null);

    try {
      const body: OpenDisputeBody = { reason: reasonText.trim() };
      const response: OpenDisputeResponse = await dealsApi.openDispute(
        state.deal.shortId,
        body,
      );
      if (!mountedRef.current) return;
      // Backend now always returns disputeId (Phase 3 backend gap fix).
      setLastSubmittedDisputeId(response.disputeId);
      haptics.success();
      // Re-fetch the deal so we transition into Phase B.
      await refresh();
    } catch (err) {
      if (!mountedRef.current) return;
      haptics.error();
      const message = isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : t('dispute.phase.open.error.submitFailed');
      setOpenError(message);
    } finally {
      if (mountedRef.current) {
        setOpenSubmitting(false);
      }
    }
  }, [isStepValid, openSubmitting, reasonText, refresh, state, t]);

  // ── Phase B (view) state ────────────────────────────────
  const [dispute, setDispute] = useState<Dispute | null>(null);
  const [disputeLoading, setDisputeLoading] = useState<boolean>(false);
  const [disputeLoadError, setDisputeLoadError] = useState<string | null>(null);

  // Fetch the dispute object whenever we land in Phase B. Two paths:
  //   1. Just opened the dispute -> `lastSubmittedDisputeId` is set, fetch
  //      directly via `getDispute(disputeId)`.
  //   2. Re-entering an existing disputed deal -> resolve via
  //      `getDisputeForDeal(shortId)` which uses the deals.dealId UNIQUE
  //      constraint to find the matching dispute.
  useEffect(() => {
    if (state.kind !== 'ready' || state.phase !== 'view') {
      setDispute(null);
      return;
    }
    let cancelled = false;
    setDisputeLoading(true);
    setDisputeLoadError(null);
    void (async (): Promise<void> => {
      try {
        const { dispute: row } = lastSubmittedDisputeId
          ? await disputesApi.getDispute(lastSubmittedDisputeId)
          : await dealsApi.getDisputeForDeal(state.deal.shortId);
        if (cancelled || !mountedRef.current) return;
        setDispute(row);
        if (!lastSubmittedDisputeId) {
          setLastSubmittedDisputeId(row.id);
        }
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        const message =
          err instanceof Error ? err.message : t('dispute.error.loading');
        setDisputeLoadError(message);
      } finally {
        if (!cancelled && mountedRef.current) {
          setDisputeLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lastSubmittedDisputeId, state, t]);

  // ── Evidence form state ─────────────────────────────────
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('text');
  const [evidenceContent, setEvidenceContent] = useState<string>('');
  const [evidenceSubmitting, setEvidenceSubmitting] = useState<boolean>(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [evidenceSuccess, setEvidenceSuccess] = useState<boolean>(false);

  // ── Evidence list state ─────────────────────────────────
  const [evidenceList, setEvidenceList] = useState<Evidence[]>([]);
  const [evidenceListLoading, setEvidenceListLoading] = useState<boolean>(false);

  const refreshEvidenceList = useCallback(async (): Promise<void> => {
    if (!dispute) return;
    setEvidenceListLoading(true);
    try {
      const { evidence } = await disputesApi.listEvidence(dispute.id);
      if (!mountedRef.current) return;
      setEvidenceList(evidence);
    } catch {
      // Non-fatal — the form still works without the list. Surface nothing
      // since the list is informational and the user can still submit.
      if (mountedRef.current) {
        setEvidenceList([]);
      }
    } finally {
      if (mountedRef.current) {
        setEvidenceListLoading(false);
      }
    }
  }, [dispute]);

  // Fetch the evidence list whenever the dispute id changes.
  useEffect(() => {
    if (!dispute) {
      setEvidenceList([]);
      return;
    }
    void refreshEvidenceList();
  }, [dispute, refreshEvidenceList]);

  const submitEvidence = useCallback(async (): Promise<void> => {
    if (!dispute) return;
    const trimmed = evidenceContent.trim();
    if (trimmed.length === 0) {
      setEvidenceError(t('dispute.phase.view.evidence.form.errorContent'));
      return;
    }
    if (trimmed.length > EVIDENCE_MAX_LENGTH) {
      setEvidenceError(t('dispute.phase.view.evidence.form.errorContent'));
      return;
    }
    if (evidenceSubmitting) return;

    haptics.medium();
    setEvidenceSubmitting(true);
    setEvidenceError(null);
    setEvidenceSuccess(false);

    try {
      const body: SubmitEvidenceBody = {
        type: evidenceType,
        content: trimmed,
      };
      await disputesApi.submitEvidence(dispute.id, body);
      if (!mountedRef.current) return;
      haptics.success();
      setEvidenceContent('');
      setEvidenceSuccess(true);
      // Refresh the list so the just-submitted item appears immediately.
      void refreshEvidenceList();
    } catch (err) {
      if (!mountedRef.current) return;
      haptics.error();
      const message = isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : t('dispute.phase.view.evidence.form.errorSubmit');
      setEvidenceError(message);
    } finally {
      if (mountedRef.current) {
        setEvidenceSubmitting(false);
      }
    }
  }, [dispute, evidenceContent, evidenceSubmitting, evidenceType, refreshEvidenceList, t]);

  // ── Telegram MainButton ─────────────────────────────────
  const mainButtonConfig = useMemo(() => {
    if (state.kind !== 'ready') return null;

    if (state.phase === 'open') {
      const isFinal = step === STEP_COUNT - 1;
      const text = isFinal
        ? openSubmitting
          ? t('dispute.phase.open.button.submitting')
          : t('dispute.phase.open.button.submit')
        : t('dispute.phase.open.button.continue');
      return {
        text,
        onClick: isFinal ? () => void submitDispute() : goNextStep,
        disabled: !isStepValid(step) || openSubmitting,
        loading: openSubmitting,
      };
    }

    // Phase B
    if (!dispute) return null;
    if (dispute.status === 'resolved' || dispute.status === 'closed') {
      return null;
    }
    return {
      text: evidenceSubmitting
        ? t('dispute.phase.view.evidence.form.submitting')
        : t('dispute.phase.view.evidence.form.submit'),
      onClick: () => void submitEvidence(),
      disabled: evidenceSubmitting || evidenceContent.trim().length === 0,
      loading: evidenceSubmitting,
    };
  }, [
    dispute,
    evidenceContent,
    evidenceSubmitting,
    goNextStep,
    isStepValid,
    openSubmitting,
    state,
    step,
    submitDispute,
    submitEvidence,
    t,
  ]);

  useMainButton(mainButtonConfig);

  // ── Render: error / loading / not-found branches ────────
  if (state.kind === 'loading') {
    return (
      <AppShell>
        <PageHeader title={t('dispute.title')} />
        <DisputeSkeleton />
      </AppShell>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <AppShell>
        <PageHeader title={t('dispute.title')} />
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="rounded-full bg-danger/15 p-3 text-danger">
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <h3 className="text-base font-semibold text-text-primary">
            {t('dispute.error.notFound')}
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              haptics.light();
              navigate(ROUTES.deals);
            }}
          >
            {t('dispute.backToDeal')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'not-disputable') {
    return (
      <AppShell>
        <PageHeader title={t('dispute.title')} />
        <Card
          className="flex flex-col items-center gap-3 py-8 text-center"
          role="alert"
        >
          <div className="rounded-full bg-warning/15 p-3 text-warning">
            <ShieldAlert className="h-6 w-6" aria-hidden />
          </div>
          <p className="max-w-sm text-sm text-text-secondary">
            {t('dispute.error.notDisputable')}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              haptics.light();
              navigate(`/deals/${shortId}`);
            }}
          >
            {t('dispute.backToDeal')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <PageHeader title={t('dispute.title')} />
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="rounded-full bg-danger/15 p-3 text-danger">
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <h3 className="text-base font-semibold text-text-primary">
            {t('dispute.error.loading')}
          </h3>
          <p className="max-w-sm text-xs text-text-muted">{state.message}</p>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  // state.kind === 'ready'
  const { deal, phase } = state;

  // ── Render: Phase A (open wizard) ───────────────────────
  if (phase === 'open') {
    return (
      <AppShell>
        <PageHeader
          title={t('dispute.phase.open.title')}
          description={deal.title}
        />

        <StepIndicator current={step} />
        <p className="-mt-4 text-center text-[10px] uppercase tracking-wide text-text-muted">
          {t('dispute.phase.open.stepIndicator', {
            current: step + 1,
            total: TOTAL_STEPS_LITERAL,
          })}
        </p>

        <div className="relative -mx-4 overflow-hidden">
          <div
            className="flex w-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${step * 100}%)` }}
          >
            {/* Step 1 — Reason selection */}
            <div className="w-full shrink-0 px-4" aria-hidden={step !== 0}>
              <Card className="flex flex-col gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('dispute.phase.open.step.reason.title')}
                  </h2>
                  <p className="mt-1 text-sm text-text-secondary">
                    {t('dispute.phase.open.step.reason.helper')}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  {REASON_PRESETS.map((preset) => (
                    <ReasonCard
                      key={preset}
                      preset={preset}
                      active={reasonPreset === preset}
                      label={t(`dispute.phase.open.reason.${preset}` as const)}
                      onSelect={handleReasonPreset}
                    />
                  ))}
                </div>

                {reasonPreset === 'other' && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="other-reason">
                      {t('dispute.phase.open.reason.other')}
                    </Label>
                    <textarea
                      id="other-reason"
                      value={reasonText}
                      maxLength={REASON_MAX_LENGTH}
                      rows={3}
                      placeholder={t(
                        'dispute.phase.open.reason.otherPlaceholder',
                      )}
                      onChange={(e) => setReasonText(e.target.value)}
                      className={cn(
                        'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                      )}
                    />
                  </div>
                )}
              </Card>
            </div>

            {/* Step 2 — Details textarea */}
            <div className="w-full shrink-0 px-4" aria-hidden={step !== 1}>
              <Card className="flex flex-col gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('dispute.phase.open.step.details.title')}
                  </h2>
                  <p className="mt-1 text-sm text-text-secondary">
                    {t('dispute.phase.open.step.details.helper')}
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reason-text">
                      {t('dispute.phase.open.step.details.title')}
                    </Label>
                    <span className="text-[10px] text-text-muted">
                      {t('dispute.phase.open.step.details.charCount', {
                        count: reasonText.length,
                        max: REASON_MAX_LENGTH,
                      })}
                    </span>
                  </div>
                  <textarea
                    id="reason-text"
                    value={reasonText}
                    maxLength={REASON_MAX_LENGTH}
                    rows={6}
                    placeholder={t(
                      'dispute.phase.open.step.details.placeholder',
                    )}
                    onChange={(e) => setReasonText(e.target.value)}
                    className={cn(
                      'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                      reasonText.trim().length > 0 &&
                        reasonText.trim().length < REASON_MIN_LENGTH &&
                        'border-danger/70',
                    )}
                  />
                  {reasonText.trim().length > 0 &&
                    reasonText.trim().length < REASON_MIN_LENGTH && (
                      <p className="text-xs text-danger">
                        {t('dispute.phase.open.error.minLength')}
                      </p>
                    )}
                </div>
              </Card>
            </div>

            {/* Step 3 — Confirm */}
            <div className="w-full shrink-0 px-4" aria-hidden={step !== 2}>
              <div className="flex flex-col gap-4">
                <Card className="flex flex-col gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary">
                      {t('dispute.phase.open.step.confirm.title')}
                    </h2>
                    <p className="mt-1 text-sm text-text-secondary">
                      {t('dispute.phase.open.step.confirm.helper')}
                    </p>
                  </div>

                  <dl className="flex flex-col gap-2 text-sm">
                    <SummaryRow
                      label={t('dispute.phase.open.step.confirm.summaryDeal')}
                      value={deal.title}
                    />
                    <SummaryRow
                      label={t('dispute.phase.open.step.confirm.summaryReason')}
                      value={reasonText.trim() || '—'}
                      multiline
                    />
                  </dl>
                </Card>

                <Card
                  className="flex items-start gap-3 border-danger/40 bg-danger/10"
                  role="alert"
                >
                  <AlertTriangle
                    className="mt-0.5 h-5 w-5 shrink-0 text-danger"
                    aria-hidden
                  />
                  <p className="text-xs leading-relaxed text-text-secondary">
                    {t('dispute.phase.open.step.confirm.disclaimer')}
                  </p>
                </Card>

                {openError !== null && (
                  <Card
                    className="border-danger/60 bg-danger/10"
                    role="alert"
                  >
                    <p className="text-sm font-semibold text-text-primary">
                      {t('dispute.phase.open.error.submitFailed')}
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">
                      {openError}
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ── Render: Phase B (view existing dispute) ─────────────
  // Status comes from the deal row first; the dispute row (if loaded)
  // refines the banner copy. When there is no dispute object yet (the
  // common case until the backend wrapper lands), we map deal.status
  // -> a synthetic dispute status so the banner still has something
  // honest to say.
  const syntheticDisputeStatus: DisputeStatus =
    deal.status === 'resolved' ? 'resolved' : 'evidence';
  const effectiveStatus: DisputeStatus = dispute?.status ?? syntheticDisputeStatus;
  const statusVariant = DISPUTE_STATUS_VARIANT[effectiveStatus];

  const buyerAmountDisplay = formatNanoString(dispute?.buyerAmount ?? null);
  const sellerAmountDisplay = formatNanoString(dispute?.sellerAmount ?? null);

  const isBuyer = currentUserId !== null && currentUserId === deal.buyerId;
  const isSeller = currentUserId !== null && currentUserId === deal.sellerId;

  return (
    <AppShell>
      <PageHeader
        title={t('dispute.phase.view.title')}
        description={deal.title}
      />

      {/* ─── Status banner ────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            #{deal.shortId}
          </h2>
          <Badge variant={statusVariant}>
            {t(`dispute.phase.view.status.${effectiveStatus}` as const)}
          </Badge>
        </div>
        <p className="text-sm text-text-secondary">
          {t(`dispute.phase.view.statusBanner.${effectiveStatus}` as const)}
        </p>

        {/* Perspective hint */}
        {(isBuyer || isSeller) && (
          <Badge variant="outline" className="self-start text-[10px]">
            {isBuyer
              ? t('dealList.role.buyer')
              : t('dealList.role.seller')}{' '}
            · {t('dealDetail.header.youLabel')}
          </Badge>
        )}
      </Card>

      {/* ─── Original reason ──────────────────────────────── */}
      {dispute?.reason && (
        <Card className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('dispute.phase.view.originalReason')}
          </h3>
          <p className="whitespace-pre-wrap text-sm text-text-secondary">
            {dispute.reason}
          </p>
          <span className="text-[10px] text-text-muted">
            {formatDateTime(dispute.createdAt)}
          </span>
        </Card>
      )}

      {/* ─── Defense banner (respondent only) ─────────────── */}
      {dispute &&
        currentUserId !== null &&
        currentUserId !== dispute.filedBy &&
        !['resolved', 'closed'].includes(dispute.status) && (
          <Card className="flex items-start gap-3 border-warning/40 bg-warning/10">
            <ShieldAlert
              className="mt-0.5 h-5 w-5 shrink-0 text-warning"
              aria-hidden
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text-primary">
                {t('dispute.phase.view.defenseBanner.title')}
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {t('dispute.phase.view.defenseBanner.body')}
              </p>
            </div>
          </Card>
        )}

      {/* ─── Message thread ──────────────────────────────── */}
      {dispute && (
        <DisputeChat
          disputeId={dispute.id}
          currentUserId={currentUserId}
          isResolved={dispute.status === 'resolved' || dispute.status === 'closed'}
        />
      )}

      {/* ─── Backend gap notice (no disputeId known) ──────── */}
      {!dispute && !disputeLoading && (
        <Card className="flex items-start gap-3 border-warning/40 bg-warning/10">
          <ShieldAlert
            className="mt-0.5 h-5 w-5 shrink-0 text-warning"
            aria-hidden
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary">
              {t('dispute.phase.view.backendGap')}
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              {t('dispute.phase.view.backendGapBody')}
            </p>
          </div>
        </Card>
      )}

      {disputeLoading && (
        <Card className="flex flex-col gap-3">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-12 w-full" />
        </Card>
      )}

      {disputeLoadError !== null && (
        <Card
          className="flex items-start gap-2 border-danger/40 bg-danger/10"
          role="alert"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-danger"
            aria-hidden
          />
          <p className="flex-1 text-xs text-text-secondary">
            {disputeLoadError}
          </p>
        </Card>
      )}

      {/* ─── Outcome card (resolved/closed) ───────────────── */}
      {dispute &&
        (dispute.status === 'resolved' || dispute.status === 'closed') &&
        dispute.outcome && (
          <Card className="flex flex-col gap-3 border-success/30 bg-success/5">
            <div className="flex items-center gap-2">
              <ShieldCheck
                className="h-5 w-5 text-success"
                aria-hidden
              />
              <h3 className="text-sm font-semibold text-text-primary">
                {t('dispute.phase.view.outcome.title')}
              </h3>
            </div>
            <p className="text-sm text-text-secondary">
              {t(
                `dispute.phase.view.outcome.${
                  dispute.outcome === 'buyer_wins'
                    ? 'buyerWins'
                    : dispute.outcome === 'seller_wins'
                      ? 'sellerWins'
                      : dispute.outcome === 'split'
                        ? 'split'
                        : 'mutualCancel'
                }` as const,
              )}
            </p>

            {dispute.outcome === 'split' && (
              <dl className="flex flex-col gap-2 border-t border-border/30 pt-3 text-sm">
                {buyerAmountDisplay && (
                  <SummaryRow
                    label={t('dispute.phase.view.outcome.buyerAmount')}
                    value={buyerAmountDisplay}
                  />
                )}
                {sellerAmountDisplay && (
                  <SummaryRow
                    label={t('dispute.phase.view.outcome.sellerAmount')}
                    value={sellerAmountDisplay}
                  />
                )}
              </dl>
            )}

            {dispute.resolutionNote && (
              <div className="border-t border-border/30 pt-3">
                <h4 className="text-[10px] uppercase tracking-wide text-text-muted">
                  {t('dispute.phase.view.outcome.resolutionNote')}
                </h4>
                <p className="mt-1 whitespace-pre-wrap text-xs text-text-secondary">
                  {dispute.resolutionNote}
                </p>
              </div>
            )}
          </Card>
        )}

      {/* ─── Evidence list ─────────────────────────────────── */}
      {dispute && (
        <Card className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('dispute.phase.view.evidence.title')}
          </h3>
          {evidenceListLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : evidenceList.length === 0 ? (
            <p className="text-xs text-text-muted">
              {t('dispute.phase.view.evidence.empty')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {evidenceList.map((item) => (
                <li
                  key={item.id}
                  className="rounded-md border border-border/30 bg-bg-glass/40 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="muted" className="uppercase">
                      {t(
                        `dispute.phase.view.evidence.form.type.${item.type}` as const,
                      )}
                    </Badge>
                    <span className="text-[10px] text-text-muted">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                  <p className="mt-2 break-words text-xs text-text-secondary">
                    {item.content}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* ─── Evidence submission form ─────────────────────── */}
      {dispute &&
        (dispute.status === 'open' || dispute.status === 'evidence') && (
          <Card className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-text-primary">
              {t('dispute.phase.view.evidence.form.title')}
            </h3>

            <div className="flex flex-col gap-2">
              <Label>{t('dispute.phase.view.evidence.form.typeLabel')}</Label>
              <div className="flex flex-wrap gap-2">
                {EVIDENCE_TYPES.map((type) => (
                  <EvidenceTypePill
                    key={type}
                    type={type}
                    active={evidenceType === type}
                    label={t(
                      `dispute.phase.view.evidence.form.type.${type}` as const,
                    )}
                    onSelect={(selected) => {
                      setEvidenceType(selected);
                      setEvidenceContent('');
                      setEvidenceError(null);
                      setEvidenceSuccess(false);
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="evidence-content">
                {t('dispute.phase.view.evidence.form.contentLabel')}
              </Label>
              {evidenceType === 'text' ? (
                <textarea
                  id="evidence-content"
                  value={evidenceContent}
                  rows={5}
                  maxLength={EVIDENCE_MAX_LENGTH}
                  placeholder={t(
                    'dispute.phase.view.evidence.form.contentPlaceholderText',
                  )}
                  onChange={(e) => {
                    setEvidenceContent(e.target.value);
                    setEvidenceError(null);
                    setEvidenceSuccess(false);
                  }}
                  className={cn(
                    'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                  )}
                />
              ) : (
                <input
                  id="evidence-content"
                  type="url"
                  value={evidenceContent}
                  maxLength={EVIDENCE_MAX_LENGTH}
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t(
                    `dispute.phase.view.evidence.form.contentPlaceholder${
                      evidenceType === 'link'
                        ? 'Link'
                        : evidenceType === 'image'
                          ? 'Image'
                          : 'File'
                    }` as const,
                  )}
                  onChange={(e) => {
                    setEvidenceContent(e.target.value);
                    setEvidenceError(null);
                    setEvidenceSuccess(false);
                  }}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                  )}
                />
              )}
              {evidenceError !== null && (
                <p className="text-xs text-danger">{evidenceError}</p>
              )}
              {evidenceSuccess && (
                <p className="text-xs text-success">
                  {t('dispute.phase.view.evidence.form.success')}
                </p>
              )}
            </div>
          </Card>
        )}
    </AppShell>
  );
}

// ─── Step 1 -> Step 2 validity guard (kept outside the page so the
//     wizard hook deps stay tight) ─────────────────────────

function isStepValidDetails(text: string): boolean {
  const len = text.trim().length;
  return len >= REASON_MIN_LENGTH && len <= REASON_MAX_LENGTH;
}

// ─── Summary row helper (mirrors NewDealWizardPage) ─────────

interface SummaryRowProps {
  label: string;
  value: string;
  multiline?: boolean;
}

function SummaryRow({ label, value, multiline }: SummaryRowProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex gap-3 border-b border-border/30 pb-2 last:border-0 last:pb-0',
        multiline ? 'flex-col' : 'items-center justify-between',
      )}
    >
      <dt className="text-[10px] uppercase tracking-wide text-text-muted">
        {label}
      </dt>
      <dd
        className={cn(
          'text-sm text-text-primary',
          multiline ? 'whitespace-pre-wrap' : 'text-right',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
